import { useEffect, useState } from 'react';
import {
  DATE_OF_BIRTH_MESSAGES,
  LEGAL_DOCUMENTS,
  USERNAME_MESSAGES,
  checkDateOfBirth,
  checkUsername,
  todayInUk,
  type ProfileSetup,
  type UsernameAvailability,
} from '@london-life/contracts';
import { Input } from '@london-life/ui/components/input';
import { Label } from '@london-life/ui/components/label';

// The profile questions every player answers once: an @username, a date of birth (18+) and the legal
// agreement. Used by the email sign-up form and by the "Complete your profile" card, so both behave the
// same. The rules are the shared ones the API enforces; the checks here only give instant feedback.
export type ProfileValues = { username: string; day: string; month: string; year: string; agreed: boolean };
export const EMPTY_PROFILE: ProfileValues = { username: '', day: '', month: '', year: '', agreed: false };

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const pad = (value: string) => value.padStart(2, '0');
export const dateOfBirthOf = (values: ProfileValues) =>
  values.day && values.month && values.year ? `${values.year}-${pad(values.month)}-${pad(values.day)}` : null;

// what is still wrong, in the words the player sees (null when everything is ready to send)
export function profileProblem(values: ProfileValues, availability: UsernameAvailability | null, askUsername = true) {
  if (askUsername) {
    const name = checkUsername(values.username.trim());
    if (!name.ok) return USERNAME_MESSAGES[name.reason];
    if (availability && !availability.available) return USERNAME_MESSAGES[availability.reason ?? 'taken'];
  }
  const dob = dateOfBirthOf(values);
  if (!dob) return 'Enter your date of birth.';
  const age = checkDateOfBirth(dob);
  if (!age.ok) return DATE_OF_BIRTH_MESSAGES[age.reason];
  if (!values.agreed) return 'Tick the box to confirm you are 18+ and agree to the documents.';
  return null;
}
export const toProfileSetup = (values: ProfileValues): ProfileSetup => ({
  username: values.username.trim(),
  dateOfBirth: dateOfBirthOf(values) ?? '',
  accepted: {
    terms: LEGAL_DOCUMENTS.terms.version,
    privacy: LEGAL_DOCUMENTS.privacy.version,
    disclaimer: LEGAL_DOCUMENTS.disclaimer.version,
  },
  adult: true,
});

type Props = {
  values: ProfileValues;
  onChange: (values: ProfileValues) => void;
  // asks the API whether a name is free; omitted when the player already has one
  checkAvailability?: (username: string) => Promise<UsernameAvailability>;
  onAvailability?: (availability: UsernameAvailability | null) => void;
  idPrefix?: string;
};

export function ProfileFields({ values, onChange, checkAvailability, onAvailability, idPrefix = 'profile' }: Props) {
  const set = (patch: Partial<ProfileValues>) => onChange({ ...values, ...patch });
  const [availability, setAvailability] = useState<UsernameAvailability | null>(null);
  const username = values.username.trim();
  const localName = checkUsername(username);

  // ask the API whether the name is free, once the player pauses typing
  useEffect(() => {
    setAvailability(null);
    onAvailability?.(null);
    if (!checkAvailability || !localName.ok) return;
    let alive = true;
    const timer = setTimeout(() => {
      checkAvailability(username)
        .then((result) => {
          if (!alive) return;
          setAvailability(result);
          onAvailability?.(result);
        })
        .catch(() => undefined);
    }, 400);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-run only when the typed name changes
  }, [username]);

  const nameHint =
    !username || !checkAvailability
      ? null
      : !localName.ok
        ? { bad: true, text: USERNAME_MESSAGES[localName.reason] }
        : availability === null
          ? { bad: false, text: 'Checking…' }
          : availability.available
            ? { bad: false, text: `@${username} is free` }
            : { bad: true, text: USERNAME_MESSAGES[availability.reason ?? 'taken'] };
  const dob = dateOfBirthOf(values);
  const age = dob ? checkDateOfBirth(dob) : null;
  const thisYear = Number(todayInUk().slice(0, 4));

  return (
    <div className="profile-fields">
      {checkAvailability && (
        <div>
          <Label htmlFor={`${idPrefix}-username`}>Username</Label>
          <div className="profile-username">
            <span aria-hidden="true">@</span>
            <Input
              id={`${idPrefix}-username`}
              value={values.username}
              onChange={(event) => set({ username: event.target.value })}
              autoComplete="username"
              maxLength={20}
              aria-invalid={nameHint?.bad || undefined}
              aria-describedby={`${idPrefix}-username-hint`}
              required
            />
          </div>
          <p
            id={`${idPrefix}-username-hint`}
            className={nameHint?.bad ? 'profile-hint bad' : 'profile-hint'}
            role="status"
          >
            {nameHint?.text ?? 'Letters, numbers and _ only. Others see this above your head and in chat.'}
          </p>
        </div>
      )}
      <fieldset>
        <legend>Date of birth</legend>
        <div className="profile-dob">
          <select aria-label="Day" value={values.day} onChange={(event) => set({ day: event.target.value })} required>
            <option value="">Day</option>
            {Array.from({ length: 31 }, (_, index) => String(index + 1)).map((day) => (
              <option key={day} value={day}>
                {day}
              </option>
            ))}
          </select>
          <select
            aria-label="Month"
            value={values.month}
            onChange={(event) => set({ month: event.target.value })}
            required
          >
            <option value="">Month</option>
            {MONTHS.map((name, index) => (
              <option key={name} value={String(index + 1)}>
                {name}
              </option>
            ))}
          </select>
          <select
            aria-label="Year"
            value={values.year}
            onChange={(event) => set({ year: event.target.value })}
            required
          >
            <option value="">Year</option>
            {Array.from({ length: 100 }, (_, index) => String(thisYear - index)).map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
        <p className={age && !age.ok ? 'profile-hint bad' : 'profile-hint'} role="status">
          {age && !age.ok ? DATE_OF_BIRTH_MESSAGES[age.reason] : 'We ask once. London Life is for adults 18+.'}
        </p>
      </fieldset>
      <label className="profile-agree">
        <input type="checkbox" checked={values.agreed} onChange={(event) => set({ agreed: event.target.checked })} />
        <span>
          I&apos;m 18+ and I agree to the{' '}
          {(['terms', 'privacy', 'disclaimer'] as const).map((key, index) => (
            <span key={key}>
              {index === 2 ? ' and ' : index === 1 ? ', ' : ''}
              <a href={LEGAL_DOCUMENTS[key].path} target="_blank" rel="noreferrer">
                {LEGAL_DOCUMENTS[key].title}
              </a>
            </span>
          ))}
          .
        </span>
      </label>
    </div>
  );
}
